<template>
	<section class="view-venue-playtherack padded">
		<ViewLayoutModuleHeader title="Beat the Rack">
			<RouterLink v-bind:to="{ name: 'PagePlaceCheckins', params: { id: venueModel.getSlug() } }">
				View Results
			</RouterLink>
		</ViewLayoutModuleHeader>

		<ChalkyPadTable
			ref="table"
			v-bind:colors="{
				cushion: '#387da8',
				cushionStroke: '#284d88',
				diamond: '#6d6e70',
				pocket: '#000000',
				rail: '#333333',
				spotInner: '#868686',
				spotOuter: '#ffffff',
				table: '#4c84b9ff',
			}"
			v-bind:diagramModel="diagramModel"
			v-bind:showOverlay="true"
		/>

		<div class="inner background-chalky-blue-4 padded rounded">
			<div class="content">
				<h5 class="title">Shoot the signature rack</h5>

				<p class="subtitle type-small-paragraph">
					Beat {{ venueModel.name }}'s signature rack in the least
					<br />
					amount of shots.
				</p>
			</div>

			<button name="btnPlay" type="submit" class="chalky button-login size-small" v-on:click="Handle_OnClickPlay">
				<span>Play</span>
			</button>
		</div>
	</section>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop, Ref } from 'vue-property-decorator';
	import { mounted } from '@/Utility/Decorators';

	/**
	 * Displays an interactive pool table diagram with play functionality.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Venue
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewVenuePlayTheRack extends ViewBase {
		/**
		 * Reference to the table component
		 *
		 * @type any
		 */
		@Ref('table')
		readonly table!: any;

		/**
		 * The venue model used for generating unique rack positions
		 *
		 * @type ChalkySticks.Model.Venue
		 */
		@Prop({
			default: () => ChalkySticks.Factory.Venue.model(),
		})
		public venueModel!: ChalkySticks.Model.Venue;

		/**
		 * The diagram model for the table display
		 *
		 * @type ChalkySticks.Model.Diagram
		 */
		protected diagramModel: ChalkySticks.Model.Diagram = ChalkySticks.Factory.Diagram.model();

		/**
		 * Generates unique rack positions based on venue ID
		 *
		 * @return any
		 */
		protected get uniqueRackPositions(): any {
			const id = this.venueModel.id;
			const seed = 'chalkysticks-venue-rack-' + id;
			const balls = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
			const positions: any[] = [];

			balls.forEach((ball: number) => {
				const x = this.createSeed(seed + '-x-' + ball, 0, 100) / 100;
				const y = this.createSeed(seed + '-y-' + ball, 0, 100) / 100;
				const position = {
					ball: ball,
					type: ball === 0 ? 'cue' : ball.toString(),
					x: x,
					y: y,
				};

				positions.push(position);
			});

			return positions;
		}

		/**
		 * Initialize the table with rack positions
		 *
		 * @return void
		 */
		@mounted
		public setup(): void {
			this.diagramModel.set({
				layout: {
					balls: this.uniqueRackPositions,
				},
			});

			this.table.tableManager.importDiagram(this.diagramModel);
		}

		/**
		 * Generates a deterministic random number between min and max (inclusive)
		 * based on a seed string. Uses the mulberry32 algorithm for better distribution.
		 *
		 * @param string seed
		 * @param number min
		 * @param number max
		 * @return number
		 */
		protected createSeed(seed: string, min: number = 0, max: number = 1): number {
			let hash = 0;

			for (let i = 0; i < seed.length; i++) {
				hash = Math.imul(hash ^ seed.charCodeAt(i), 0x5bd1e995);
				hash ^= hash >>> 15;
			}

			let t = (hash += 0x6d2b79f5);
			t = Math.imul(t ^ (t >>> 15), t | 1);
			t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
			const normalized = ((t ^ (t >>> 14)) >>> 0) / 0x100000000;

			return Math.floor(normalized * (max - min + 1)) + min;
		}

		/**
		 * Returns the inverse rack positions for the game
		 *
		 * @return any[]
		 */
		public getInverseRackPositions(): any[] {
			return this.uniqueRackPositions.map((position: any) => {
				return {
					ball: position.ball,
					type: position.type,
					x: 1 - position.x,
					y: 1 - position.y,
				};
			});
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Emits play event to parent to start the game
		 *
		 * @param MouseEvent e
		 * @return void
		 */
		protected Handle_OnClickPlay(e: MouseEvent): void {
			this.$emit('play');
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-venue-playtherack {
		.inner {
			align-items: center;
			display: flex;
			justify-content: space-between;
		}
	}
</style>
